import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  TrendingUp, 
  Target, 
  Users, 
  CheckCircle,
  BarChart3,
  Download
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { batchService, Batch, GrowthRecord } from '../services/batchService';
import { useUser } from '../contexts/UserContext';

// Official Cobb 500 Performance Standards (2022 Performance & Nutrition Supplement)
// Source: https://www.cobbgenetics.com/assets/Cobb-Files/2022-Cobb500-Broiler-Performance-Nutrition-Supplement.pdf
const COBB_500_OFFICIAL_STANDARDS = {
  weight: [
    { age: 0, weight: 0.042, description: 'Day 0 - Placement' },
    { age: 1, weight: 0.045, description: 'Day 1' },
    { age: 2, weight: 0.050, description: 'Day 2' },
    { age: 3, weight: 0.055, description: 'Day 3' },
    { age: 4, weight: 0.060, description: 'Day 4' },
    { age: 5, weight: 0.065, description: 'Day 5' },
    { age: 6, weight: 0.070, description: 'Day 6' },
    { age: 7, weight: 0.165, description: 'Week 1' },
    { age: 8, weight: 0.180, description: 'Day 8' },
    { age: 9, weight: 0.195, description: 'Day 9' },
    { age: 10, weight: 0.210, description: 'Day 10' },
    { age: 11, weight: 0.225, description: 'Day 11' },
    { age: 12, weight: 0.240, description: 'Day 12' },
    { age: 13, weight: 0.255, description: 'Day 13' },
    { age: 14, weight: 0.420, description: 'Week 2' },
    { age: 15, weight: 0.450, description: 'Day 15' },
    { age: 16, weight: 0.480, description: 'Day 16' },
    { age: 17, weight: 0.510, description: 'Day 17' },
    { age: 18, weight: 0.540, description: 'Day 18' },
    { age: 19, weight: 0.570, description: 'Day 19' },
    { age: 20, weight: 0.600, description: 'Day 20' },
    { age: 21, weight: 0.711, description: 'Week 3 - Research Standard' },
    { age: 22, weight: 0.760, description: 'Day 22' },
    { age: 23, weight: 0.810, description: 'Day 23' },
    { age: 24, weight: 0.860, description: 'Day 24' },
    { age: 25, weight: 0.910, description: 'Day 25' },
    { age: 26, weight: 0.960, description: 'Day 26' },
    { age: 27, weight: 1.010, description: 'Day 27' },
    { age: 28, weight: 1.350, description: 'Week 4' },
    { age: 29, weight: 1.420, description: 'Day 29' },
    { age: 30, weight: 1.490, description: 'Day 30' },
    { age: 31, weight: 1.560, description: 'Day 31' },
    { age: 32, weight: 1.630, description: 'Day 32' },
    { age: 33, weight: 1.700, description: 'Day 33' },
    { age: 34, weight: 1.770, description: 'Day 34' },
    { age: 35, weight: 1.840, description: 'Day 35' },
    { age: 36, weight: 1.910, description: 'Day 36' },
    { age: 37, weight: 1.980, description: 'Day 37' },
    { age: 38, weight: 2.050, description: 'Day 38' },
    { age: 39, weight: 2.120, description: 'Day 39' },
    { age: 40, weight: 2.190, description: 'Day 40' },
    { age: 41, weight: 2.260, description: 'Day 41' },
    { age: 42, weight: 2.330, description: 'Day 42' },
    { age: 43, weight: 2.400, description: 'Day 43' },
    { age: 44, weight: 2.470, description: 'Day 44' },
    { age: 45, weight: 2.540, description: 'Day 45' },
    { age: 46, weight: 2.610, description: 'Day 46' },
    { age: 47, weight: 2.680, description: 'Day 47' },
    { age: 48, weight: 2.750, description: 'Day 48' },
    { age: 49, weight: 2.820, description: 'Day 49' },
    { age: 50, weight: 2.890, description: 'Day 50' },
    { age: 51, weight: 2.960, description: 'Day 51' },
    { age: 52, weight: 3.030, description: 'Day 52' },
    { age: 53, weight: 3.100, description: 'Day 53' },
    { age: 54, weight: 3.170, description: 'Day 54' },
    { age: 55, weight: 3.240, description: 'Day 55' },
    { age: 56, weight: 3.310, description: 'Day 56' }
  ],
  fcr: [
    { age: 0, fcr: 0, description: 'Placement' },
    { age: 7, fcr: 0.73, description: 'Week 1' },
    { age: 14, fcr: 0.83, description: 'Week 2' },
    { age: 21, fcr: 0.88, description: 'Week 3' },
    { age: 28, fcr: 0.93, description: 'Week 4' },
    { age: 35, fcr: 0.97, description: 'Week 5' },
    { age: 42, fcr: 1.06, description: 'Week 6' },
    { age: 49, fcr: 1.15, description: 'Week 7' }
  ],
  // Performance targets based on official Cobb 500 standards
  performanceTargets: {
    mortality: {
      week1: 1.0,    // 1% mortality in week 1
      week2: 0.5,    // 0.5% mortality in week 2
      week3: 0.3,    // 0.3% mortality in week 3
      week4: 0.2,    // 0.2% mortality in week 4
      week5: 0.2,    // 0.2% mortality in week 5
      week6: 0.2,    // 0.2% mortality in week 6
      total: 2.4     // Total mortality target: 2.4%
    },
    uniformity: {
      excellent: 95,  // 95%+ uniformity
      good: 90,       // 90-94% uniformity
      fair: 85,       // 85-89% uniformity
      poor: 85        // <85% uniformity
    }
  }
};

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const StatDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const FormContainer = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  margin-bottom: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #28a745;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #28a745;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #28a745;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const RecordsTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  margin-bottom: 24px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
`;

const TableTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f8f9fa;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px 24px;
  text-align: left;
  font-weight: 600;
  color: #666;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: 16px 24px;
  color: #333;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const ChartContainer = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  margin-bottom: 24px;
`;

const ChartHeader = styled.div`
  margin-bottom: 24px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #333;
  font-size: 20px;
  font-weight: 600;
`;

const ChartSubtitle = styled.p`
  margin: 0;
  color: #666;
  font-size: 14px;
`;

const PerformanceSection = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  margin-bottom: 24px;
`;

const PerformanceTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 20px;
  font-weight: 600;
`;

const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const PerformanceCard = styled.div`
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
`;

const PerformanceHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 12px;
`;

const BatchName = styled.h4`
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
`;

const PerformanceValue = styled.div<{ performance: number }>`
  font-size: 24px;
  font-weight: 700;
  color: ${props => {
    if (props.performance >= 100) return '#28a745'; // Green for above standard
    if (props.performance >= 90) return '#ffc107'; // Yellow for close to standard
    return '#dc3545'; // Red for below standard
  }};
`;

const PerformanceLabel = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 4px;
`;

const GrowthTrackerAdvanced: React.FC = () => {
  console.log('GrowthTrackerAdvanced: Component rendering...');
  const { canDeleteAll } = useUser();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [growthRecords, setGrowthRecords] = useState<GrowthRecord[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    flock: '',
    age: '',
    weight: '',
    feedConsumption: '',
    mortality: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    recordingMethod: 'individual' as 'individual' | 'sample'
  });
  
  // Load batches and growth records on component mount
  useEffect(() => {
    console.log('GrowthTrackerAdvanced: Loading data...');
    try {
      const loadedBatches = batchService.loadBatches();
      console.log('GrowthTrackerAdvanced: Loaded batches:', loadedBatches.length);
      setBatches(loadedBatches);
      
      // Load growth records from all batches
      const allRecords: GrowthRecord[] = [];
      loadedBatches.forEach(batch => {
        if (batch.growthRecords) {
          allRecords.push(...batch.growthRecords);
        }
      });
      console.log('GrowthTrackerAdvanced: Loaded growth records:', allRecords.length);
      setGrowthRecords(allRecords);
    } catch (error) {
      console.error('GrowthTrackerAdvanced: Error loading data:', error);
      setBatches([]);
      setGrowthRecords([]);
    }
  }, []);

  // Get available batches for form
  const getAvailableBatches = (): string[] => {
    try {
      return batches
        .filter(batch => batch.status === 'active')
        .map(batch => batch.name)
        .sort();
    } catch (error) {
      console.error('Error getting available batches:', error);
      return [];
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Find the batch for this flock
      const batch = batches.find(b => b.name === formData.flock);
      if (!batch) {
        alert('Error: Batch not found!');
        return;
      }

      // Create new growth record
      const newRecord: GrowthRecord = {
        id: Date.now().toString(),
        tenantId: batch.tenantId,
        batchId: batch.id,
        flock: formData.flock,
        age: parseInt(formData.age),
        weight: parseFloat(formData.weight),
        date: formData.date,
        feedConsumption: parseFloat(formData.feedConsumption) || 0,
        mortality: parseFloat(formData.mortality) || 0,
        notes: formData.notes,
        recordingMethod: formData.recordingMethod,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Update the batch with the new growth record
      const updatedBatch = {
        ...batch,
        growthRecords: [...(batch.growthRecords || []), newRecord],
        lastUpdated: new Date().toISOString()
      };

      // Save to batch service
      batchService.updateBatch(batch.id, updatedBatch);
      
      // Update local state
      setBatches(prevBatches => 
        prevBatches.map(b => b.id === batch.id ? updatedBatch : b)
      );
      setGrowthRecords(prevRecords => [...prevRecords, newRecord]);
      
      alert('Weight record added successfully!');
      
      // Reset form
      setFormData({
        flock: '',
        age: '',
        weight: '',
        feedConsumption: '',
        mortality: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
        recordingMethod: 'individual'
      });
      setShowForm(false);
    } catch (error) {
      console.error('Error adding growth record:', error);
      alert('Error adding weight record: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const availableBatches = getAvailableBatches();

  // Calculate statistics
  const stats = useMemo(() => {
    try {
      const totalRecords = growthRecords.length;
      const activeBatches = batches.filter(batch => batch.status === 'active').length;
      const totalBirds = batches.reduce((sum, batch) => sum + batch.currentBirds, 0);
      const averageWeight = totalRecords > 0 
        ? growthRecords.reduce((sum, record) => sum + record.weight, 0) / totalRecords 
        : 0;

      return {
        totalRecords,
        activeBatches,
        totalBirds,
        averageWeight: Math.round(averageWeight * 100) / 100
      };
    } catch (error) {
      console.error('Error calculating stats:', error);
      return {
        totalRecords: 0,
        activeBatches: 0,
        totalBirds: 0,
        averageWeight: 0
      };
    }
  }, [growthRecords, batches]);

  // Generate batch colors for chart
  const batchColors = useMemo(() => {
    const colors = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4', '#ffeb3b', '#795548'];
    const colorMap: { [key: string]: string } = {};
    batches.forEach((batch, index) => {
      colorMap[batch.name] = colors[index % colors.length];
    });
    return colorMap;
  }, [batches]);

  // Function to get standard weight for a given age
  const getStandardWeight = (age: number): number => {
    const standard = COBB_500_OFFICIAL_STANDARDS.weight.find(s => s.age === age);
    return standard ? standard.weight : 0;
  };

  // Function to get standard FCR for a given age
  const getStandardFCR = (age: number): number => {
    const standard = COBB_500_OFFICIAL_STANDARDS.fcr.find(s => s.age === age);
    return standard ? standard.fcr : 0;
  };

  // Function to get mortality target for a given week
  const getMortalityTarget = (week: number): number => {
    const targets = COBB_500_OFFICIAL_STANDARDS.performanceTargets.mortality;
    switch (week) {
      case 1: return targets.week1;
      case 2: return targets.week2;
      case 3: return targets.week3;
      case 4: return targets.week4;
      case 5: return targets.week5;
      case 6: return targets.week6;
      default: return targets.total;
    }
  };

  // Prepare chart data with standards
  const chartData = useMemo(() => {
    const allAges = new Set<number>();
    
    // Collect all ages from growth records
    growthRecords.forEach(record => {
      allAges.add(record.age);
    });
    
    // Convert to array and sort
    const sortedAges = Array.from(allAges).sort((a, b) => a - b);
    
    // Create chart data points
    return sortedAges.map(age => {
      const dataPoint: any = { age };
      
      // Add standard weight
      dataPoint.standard = getStandardWeight(age);
      
      // Add actual weights for each batch
      availableBatches.forEach(batchName => {
        const batchRecords = growthRecords.filter(r => r.flock === batchName && r.age === age);
        if (batchRecords.length > 0) {
          // Use average weight if multiple records for same age
          const avgWeight = batchRecords.reduce((sum, r) => sum + r.weight, 0) / batchRecords.length;
          dataPoint[batchName] = avgWeight;
        }
      });
      
      return dataPoint;
    });
  }, [growthRecords, availableBatches]);

  // Calculate comprehensive performance against standards
  const batchPerformance = useMemo(() => {
    return availableBatches.map(batchName => {
      const batchRecords = growthRecords.filter(r => r.flock === batchName);
      if (batchRecords.length === 0) return null;

      // Sort records once by age (descending for latest, ascending for trend)
      const sortedByAgeDesc = [...batchRecords].sort((a, b) => b.age - a.age);
      const sortedByAgeAsc = [...batchRecords].sort((a, b) => a.age - b.age);
      
      const latestRecord = sortedByAgeDesc[0];
      const standardWeight = getStandardWeight(latestRecord.age);
      const standardFCR = getStandardFCR(latestRecord.age);
      
      if (standardWeight === 0) return null;

      // Calculate weight performance
      const weightPerformance = (latestRecord.weight / standardWeight) * 100;
      
      // Calculate FCR if feed consumption is available
      let fcrPerformance = null;
      let currentFCR = null;
      if (latestRecord.feedConsumption > 0 && latestRecord.weight > 0) {
        currentFCR = latestRecord.feedConsumption / latestRecord.weight;
        if (standardFCR > 0) {
          fcrPerformance = (standardFCR / currentFCR) * 100; // Lower FCR is better
        }
      }
      
      // Calculate mortality rate
      const totalMortality = batchRecords.reduce((sum, r) => sum + (r.mortality || 0), 0);
      const week = Math.ceil(latestRecord.age / 7);
      const mortalityTarget = getMortalityTarget(week);
      
      // Calculate uniformity (weight range analysis)
      const weights = batchRecords.map(r => r.weight).filter(w => w > 0);
      let uniformity = null;
      if (weights.length > 1) {
        const avgWeight = weights.reduce((sum, w) => sum + w, 0) / weights.length;
        const weightRange = Math.max(...weights) - Math.min(...weights);
        uniformity = Math.max(0, 100 - (weightRange / avgWeight) * 100);
      }
      
      // Calculate growth trend
      let growthTrend = 'stable';
      if (sortedByAgeAsc.length >= 2) {
        const recentGrowth = sortedByAgeAsc.slice(-3);
        if (recentGrowth.length >= 2) {
          const growthRate = (recentGrowth[recentGrowth.length - 1].weight - recentGrowth[0].weight) / 
                           (recentGrowth[recentGrowth.length - 1].age - recentGrowth[0].age);
          if (growthRate > 0.05) growthTrend = 'increasing';
          else if (growthRate < -0.02) growthTrend = 'decreasing';
        }
      }
      
      return {
        batchName,
        currentWeight: latestRecord.weight,
        standardWeight,
        weightPerformance: Math.round(weightPerformance * 10) / 10,
        currentFCR,
        standardFCR,
        fcrPerformance: fcrPerformance ? Math.round(fcrPerformance * 10) / 10 : null,
        totalMortality,
        mortalityTarget,
        mortalityRate: totalMortality > 0 ? (totalMortality / batchRecords.length) * 100 : 0,
        uniformity: uniformity ? Math.round(uniformity * 10) / 10 : null,
        growthTrend,
        age: latestRecord.age,
        recordCount: batchRecords.length,
        lastUpdate: latestRecord.date
      };
    }).filter(Boolean);
  }, [growthRecords, availableBatches]);

  // Export data functionality
  const handleExportData = () => {
    try {
      const data = {
        batches: batches,
        growthRecords: growthRecords,
        exportDate: new Date().toISOString(),
        version: '1.0'
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `growth-tracking-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Clear all batches functionality
  const clearAllBatches = () => {
    try {
      if (window.confirm('Are you sure you want to clear all batches? This action cannot be undone.')) {
        batchService.clearAllBatches();
        setBatches([]);
        setGrowthRecords([]);
        alert('All batches cleared successfully!');
      }
    } catch (error) {
      console.error('Error clearing batches:', error);
      alert('Error clearing batches: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };
  
  return (
    <Container>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="#2E7D32">
              <TrendingUp size={24} />
            </StatIcon>
            <StatTitle>Total Records</StatTitle>
          </StatHeader>
          <StatValue>{stats.totalRecords}</StatValue>
          <StatDescription>Weight measurements recorded</StatDescription>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#2E7D32">
              <Users size={24} />
            </StatIcon>
            <StatTitle>Active Batches</StatTitle>
          </StatHeader>
          <StatValue>{stats.activeBatches}</StatValue>
          <StatDescription>Currently being tracked</StatDescription>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#FF8C00">
              <Target size={24} />
            </StatIcon>
            <StatTitle>Total Birds</StatTitle>
          </StatHeader>
          <StatValue>{stats.totalBirds.toLocaleString()}</StatValue>
          <StatDescription>Across all active batches</StatDescription>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#E53E3E">
              <BarChart3 size={24} />
            </StatIcon>
            <StatTitle>Average Weight</StatTitle>
          </StatHeader>
          <StatValue>{stats.averageWeight} kg</StatValue>
          <StatDescription>Across all records</StatDescription>
        </StatCard>
      </StatsGrid>

      <ChartContainer>
        <ChartHeader>
          <ChartTitle>Growth Tracking Chart</ChartTitle>
          <ChartSubtitle>Weight progression by flock over time</ChartSubtitle>
        </ChartHeader>
        
        {growthRecords.length > 0 ? (
          <div style={{ position: 'relative', height: '400px', margin: '20px 0' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="age" 
                  stroke="#666"
                  fontSize={12}
                  tickFormatter={(value: any) => `Day ${value}`}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  tickFormatter={(value: any) => `${value}kg`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value: any, name: any) => [
                    `${value} kg`, 
                    name === 'standard' ? 'Cobb 500 Standard' : `Flock: ${name}`
                  ]}
                  labelFormatter={(label: any) => `Age: ${label} days`}
                />
                <Legend />
                
                {/* Standard Line */}
                <Line
                  type="monotone"
                  dataKey="standard"
                  stroke="#ff6b6b"
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={{ fill: '#ff6b6b', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, stroke: '#ff6b6b', strokeWidth: 2 }}
                  name="Cobb 500 Standard"
                  connectNulls={false}
                />
                
                {/* Batch Lines */}
                {availableBatches.map(batchName => (
                  <Line
                    key={batchName}
                    type="monotone"
                    dataKey={batchName}
                    stroke={batchColors[batchName] || '#2196f3'}
                    strokeWidth={2}
                    dot={{ fill: batchColors[batchName] || '#2196f3', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: batchColors[batchName] || '#2196f3', strokeWidth: 2 }}
                    name={batchName}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: '12px',
            margin: '20px 0'
          }}>
            <div style={{ 
              fontSize: '64px', 
              marginBottom: '20px',
              opacity: 0.6
            }}>
              📈
            </div>
            <h3 style={{ 
              color: '#333', 
              marginBottom: '16px',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              No Growth Records Yet
            </h3>
            <p style={{ 
              color: '#666', 
              fontSize: '16px', 
              marginBottom: '32px',
              maxWidth: '400px',
              margin: '0 auto 32px auto',
              lineHeight: '1.6'
            }}>
              Start recording weight measurements to see your flock's growth progress visualized on the chart.
            </p>
            <Button 
              onClick={() => setShowForm(true)}
              style={{ 
                backgroundColor: '#28a745',
                color: 'white',
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Record First Weight
            </Button>
          </div>
        )}

        {/* Batch Legend */}
        {availableBatches.length > 0 && (
          <div style={{ 
            marginTop: '20px', 
            padding: '16px', 
            background: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h5 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '0.9rem' }}>Batch Legend</h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {availableBatches.map(batch => (
                <div key={batch} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    background: batch ? (batchColors[batch] || '#2196f3') : '#2196f3', 
                    borderRadius: '50%' 
                  }} />
                  <span style={{ color: '#666' }}>{batch}</span>
                  <span style={{ color: '#999', fontSize: '0.8rem' }}>
                    ({growthRecords.filter(r => r.flock === batch).length} records)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </ChartContainer>

      {/* Comprehensive Performance Analysis Section */}
      {batchPerformance.length > 0 && (
        <PerformanceSection>
          <PerformanceTitle>Comprehensive Performance Analysis vs Cobb 500 Standards</PerformanceTitle>
          <PerformanceGrid>
            {batchPerformance.map((perf: any) => (
              <PerformanceCard key={perf.batchName}>
                <PerformanceHeader>
                  <BatchName>{perf.batchName}</BatchName>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    marginTop: '4px'
                  }}>
                    Age: {perf.age} days • {perf.recordCount} records
                  </div>
                </PerformanceHeader>
                
                {/* Weight Performance */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Weight Performance</div>
                  <PerformanceValue performance={perf.weightPerformance}>
                    {perf.weightPerformance}%
                  </PerformanceValue>
                  <PerformanceLabel>
                    {perf.currentWeight.toFixed(2)}kg vs {perf.standardWeight.toFixed(2)}kg standard
                  </PerformanceLabel>
                  <div style={{ 
                    fontSize: '12px', 
                    color: perf.weightPerformance >= 100 ? '#28a745' : perf.weightPerformance >= 90 ? '#ffc107' : '#dc3545',
                    fontWeight: '600',
                    marginTop: '4px'
                  }}>
                    {perf.weightPerformance >= 100 ? '✅ Above Standard' : 
                     perf.weightPerformance >= 90 ? '⚠️ Close to Standard' : 
                     '❌ Below Standard'}
                  </div>
                </div>

                {/* FCR Performance */}
                {perf.currentFCR && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Feed Conversion Ratio</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: perf.fcrPerformance && perf.fcrPerformance >= 100 ? '#28a745' : '#ffc107' }}>
                      {perf.currentFCR.toFixed(2)}
                    </div>
                    <PerformanceLabel>
                      vs {perf.standardFCR.toFixed(2)} standard
                    </PerformanceLabel>
                    {perf.fcrPerformance && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: perf.fcrPerformance >= 100 ? '#28a745' : '#ffc107',
                        fontWeight: '600',
                        marginTop: '4px'
                      }}>
                        {perf.fcrPerformance >= 100 ? '✅ Better than Standard' : '⚠️ Needs Improvement'}
                      </div>
                    )}
                  </div>
                )}

                {/* Mortality Analysis */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Mortality</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: perf.mortalityRate <= perf.mortalityTarget ? '#28a745' : '#dc3545' }}>
                    {perf.totalMortality} birds
                  </div>
                  <PerformanceLabel>
                    Rate: {perf.mortalityRate.toFixed(1)}% vs {perf.mortalityTarget}% target
                  </PerformanceLabel>
                  <div style={{ 
                    fontSize: '12px', 
                    color: perf.mortalityRate <= perf.mortalityTarget ? '#28a745' : '#dc3545',
                    fontWeight: '600',
                    marginTop: '4px'
                  }}>
                    {perf.mortalityRate <= perf.mortalityTarget ? '✅ Within Target' : '❌ Above Target'}
                  </div>
                </div>

                {/* Uniformity Analysis */}
                {perf.uniformity && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Uniformity</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: perf.uniformity >= 90 ? '#28a745' : perf.uniformity >= 85 ? '#ffc107' : '#dc3545' }}>
                      {perf.uniformity}%
                    </div>
                    <PerformanceLabel>
                      Weight consistency across flock
                    </PerformanceLabel>
                    <div style={{ 
                      fontSize: '12px', 
                      color: perf.uniformity >= 90 ? '#28a745' : perf.uniformity >= 85 ? '#ffc107' : '#dc3545',
                      fontWeight: '600',
                      marginTop: '4px'
                    }}>
                      {perf.uniformity >= 90 ? '✅ Excellent' : 
                       perf.uniformity >= 85 ? '⚠️ Good' : '❌ Needs Improvement'}
                    </div>
                  </div>
                )}

                {/* Growth Trend */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Growth Trend</div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '600',
                    color: perf.growthTrend === 'increasing' ? '#28a745' : 
                           perf.growthTrend === 'decreasing' ? '#dc3545' : '#666'
                  }}>
                    {perf.growthTrend === 'increasing' ? '📈 Increasing' : 
                     perf.growthTrend === 'decreasing' ? '📉 Decreasing' : '➡️ Stable'}
                  </div>
                </div>

                {/* Last Update */}
                <div style={{ 
                  fontSize: '12px', 
                  color: '#666',
                  borderTop: '1px solid #e9ecef',
                  paddingTop: '8px',
                  marginTop: '8px'
                }}>
                  Last updated: {new Date(perf.lastUpdate).toLocaleDateString()}
                </div>
              </PerformanceCard>
            ))}
          </PerformanceGrid>
        </PerformanceSection>
      )}

      <ActionBar>
        <Button 
          onClick={() => setShowForm(!showForm)}
          style={{ 
            backgroundColor: showForm ? '#dc3545' : '#28a745',
            color: 'white'
          }}
        >
          <Plus size={20} />
          {showForm ? 'Cancel' : 'Record Weight Measurements'}
        </Button>
        <Button 
          onClick={handleExportData}
          style={{ 
            backgroundColor: '#6c757d',
            color: 'white'
          }}
        >
          <Download size={20} />
          Export Data
        </Button>
        {canDeleteAll && (
          <Button 
            onClick={clearAllBatches}
            style={{ 
              backgroundColor: '#dc3545',
              color: 'white'
            }}
          >
            Clear All Batches
          </Button>
        )}
      </ActionBar>

      {showForm && (
        <FormContainer>
          <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Record Individual Weight Measurements</h3>
          
          {/* Recording Method Info */}
          <div style={{ 
            background: '#e3f2fd', 
            padding: '16px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #bbdefb'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#1976d2', fontSize: '16px' }}>Recording Method</h4>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
              Choose how you're recording weights:
            </p>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666', fontSize: '14px' }}>
              <li><strong>Individual:</strong> Weighing each bird separately (most accurate)</li>
              <li><strong>Sample:</strong> Weighing a representative sample of birds</li>
            </ul>
          </div>
          
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormGroup>
                <Label htmlFor="flock">Flock/Batch *</Label>
                <Select
                  id="flock"
                  value={formData.flock}
                  onChange={(e) => setFormData({ ...formData, flock: e.target.value })}
                  required
                >
                  <option value="">Select a flock</option>
                  {availableBatches.map(batch => (
                    <option key={batch} value={batch}>{batch}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="age">Age (days) *</Label>
                <Input
                  id="age"
                  type="number"
                  min="0"
                  max="365"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  placeholder="Enter age in days"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="weight">Weight (kg) *</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="10"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  placeholder="Enter weight in kg"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="feedConsumption">Feed Consumption (kg)</Label>
                <Input
                  id="feedConsumption"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.feedConsumption}
                  onChange={(e) => setFormData({ ...formData, feedConsumption: e.target.value })}
                  placeholder="Enter feed consumption"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="mortality">Mortality Count</Label>
                <Input
                  id="mortality"
                  type="number"
                  min="0"
                  value={formData.mortality}
                  onChange={(e) => setFormData({ ...formData, mortality: e.target.value })}
                  placeholder="Enter mortality count"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="recordingMethod">Recording Method *</Label>
                <Select
                  id="recordingMethod"
                  value={formData.recordingMethod}
                  onChange={(e) => setFormData({ ...formData, recordingMethod: e.target.value as 'individual' | 'sample' })}
                  required
                >
                  <option value="individual">Individual Weighing</option>
                  <option value="sample">Sample Weighing</option>
                </Select>
              </FormGroup>

              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <TextArea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any additional notes about this measurement..."
                  rows={3}
                />
              </FormGroup>
            </FormGrid>

            <FormActions>
              <Button 
                type="submit"
                style={{ 
                  backgroundColor: '#28a745',
                  color: 'white'
                }}
              >
                <CheckCircle size={20} />
                Add Weight Record
              </Button>
              <Button 
                type="button"
                onClick={() => setShowForm(false)}
                style={{ 
                  backgroundColor: '#6c757d',
                  color: 'white'
                }}
              >
                Cancel
              </Button>
            </FormActions>
          </form>
        </FormContainer>
      )}

      {/* Growth Records Table */}
      <RecordsTable>
            <TableHeader>
          <TableTitle>Growth Records</TableTitle>
        </TableHeader>
        
        {growthRecords.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Flock</TableHeaderCell>
                <TableHeaderCell>Age (days)</TableHeaderCell>
                <TableHeaderCell>Weight (kg)</TableHeaderCell>
                <TableHeaderCell>Feed (kg)</TableHeaderCell>
                <TableHeaderCell>Mortality</TableHeaderCell>
                <TableHeaderCell>FCR</TableHeaderCell>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Method</TableHeaderCell>
                <TableHeaderCell>Notes</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {growthRecords
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((record) => (
                <TableRow key={record.id}>
                    <TableCell>{record.flock}</TableCell>
                  <TableCell>{record.age}</TableCell>
                    <TableCell>{record.weight.toFixed(2)}</TableCell>
                    <TableCell>{record.feedConsumption ? record.feedConsumption.toFixed(2) : '-'}</TableCell>
                    <TableCell>{record.mortality || '-'}</TableCell>
                    <TableCell>
                      {record.feedConsumption && record.feedConsumption > 0 && record.weight > 0 
                        ? (record.feedConsumption / record.weight).toFixed(2) 
                        : '-'}
                    </TableCell>
                    <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: (record.recordingMethod || 'individual') === 'individual' ? '#e3f2fd' : '#fff3e0',
                        color: (record.recordingMethod || 'individual') === 'individual' ? '#1976d2' : '#f57c00'
                      }}>
                        {(record.recordingMethod || 'individual') === 'individual' ? 'Individual' : 'Sample'}
                      </span>
                    </TableCell>
                    <TableCell>{record.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>
            <EmptyIcon>📊</EmptyIcon>
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>No Growth Records Yet</h3>
            <p style={{ margin: '0', color: '#666' }}>
              Start recording weight measurements to see your flock's growth data here.
            </p>
          </EmptyState>
        )}
      </RecordsTable>
    </Container>
  );
};

export default GrowthTrackerAdvanced;